import React, { useState, useRef, useMemo } from "react";
import { Button, Collapse, Tooltip, Input, Empty, App } from "antd";
import { SearchOutlined, SaveOutlined, RocketOutlined } from '@ant-design/icons';
import { styled } from 'styled-components';
import useEditorStore from "@/store/useEditorStore";

const { Panel } = Collapse;

const Item = styled.a<{ $move }>`
  svg {
    width: 22px;
    height: 22px;
    fill: #707070;
  }
  ${(props) =>
    props.$move &&
    `
    cursor: move;
  `}
`;

const StyledContainer = styled.div`
  .palette-collapse .ant-collapse-header {
    padding: 8px 12px !important;
    background-color: #f5f5f5;
    border-radius: 4px !important;
  }
  
  .palette-collapse .ant-collapse-content-box {
    padding: 8px 4px !important;
  }
  
  .red-ui-palette-node {
    margin-bottom: 6px;
    border-radius: 4px;
    position: relative;
  }
  
  .red-ui-palette-node:hover {
    opacity: 0.9;
  }
  
  .palette-collapse {
    max-height: 100%;
    overflow: visible;
  }
  
  .ant-collapse-content {
    overflow: visible !important;
  }
`;

function DraggableComponent({nodeType, children }) {
    const [dragged, setDragged] = useState(false);
    const ref = useRef(null);

    const handleDragStart = (e) => {
      e.dataTransfer.effectAllowed = 'move';
      const rect = ref.current.getBoundingClientRect();
      const startX = rect.left;
      const startY = rect.top;
      e.dataTransfer.setData("nodeType", nodeType);
    };

    return (
      <div
        ref={ref}
        draggable
        onDragStart={handleDragStart}
        onDragEnter={e => e.preventDefault()}
        onDragOver={e => e.preventDefault()}
      >
        {children}
      </div>
    );
}

// 节点分类配置
const NODE_CATEGORIES = {
  'basic': {
    title: '基础组件',
    key: 'basic',
    types: ['inject', 'lua', 'http_handler', 'request', 'response', 'writefile', 'debug', 'switch', 'comment']
  },
  'communication': {
    title: '通信组件',
    key: 'communication',
    types: ['mqtt-in', 'mqtt-out', 'wechat_pay', 'modbus-client']
  },
  'auth': {
    title: '认证组件',
    key: 'auth',
    types: ['gs_auth']
  },
  'database': {
    title: '数据库',
    key: 'database',
    types: ['redis-cmd']
  },
  'other': {
    title: '其他组件',
    key: 'other',
    types: []
  }
};

function Palette({ onDragStart, onDragEnd }) {
  const save = useEditorStore.use.save();
  const deploy = useEditorStore.use.deploy();
  const registerNodes = useEditorStore.use.registerNodes();
  const [searchText, setSearchText] = useState('');
  const [activeKeys, setActiveKeys] = useState(['basic']);

  // 根据搜索文本过滤节点
  const filteredNodes = useMemo(() => {
    return registerNodes.filter(node =>
      node?.drawable !== false &&
      (searchText === '' || node.type.toLowerCase().includes(searchText.toLowerCase()))
    );
  }, [registerNodes, searchText]);

  // 将节点按类别分组
  const categorizedNodes = useMemo(() => {
    const result = {};

    // 初始化所有类别
    Object.keys(NODE_CATEGORIES).forEach(category => {
      result[category] = [];
    });

    // 分配节点到对应类别
    filteredNodes.forEach(node => {
      let assigned = false;

      // 尝试根据类型匹配到预定义类别
      for (const [category, config] of Object.entries(NODE_CATEGORIES)) {
        if (config.types.includes(node.type)) {
          result[category].push(node);
          assigned = true;
          break;
        }
      }

      // 如果没有匹配到任何类别，放入"其他"类别
      if (!assigned) {
        result.other.push(node);
      }
    });

    return result;
  }, [filteredNodes]);

  // 动态生成折叠面板的内容
  const collapseItems = useMemo(() => {
    return Object.entries(NODE_CATEGORIES).map(([key, category]) => {
      const nodes = categorizedNodes[key];

      // 如果该类别没有节点且有搜索条件，不显示该类别
      if (nodes.length === 0 && searchText !== '') {
        return null;
      }

      return {
        key: category.key,
        label: (
          <div className="flex justify-between items-center">
            <span>{category.title}</span>
            <span className="text-xs text-gray-400">{nodes.length}</span>
          </div>
        ),
        children: nodes.length > 0 ? (
          <div className="space-y-2">
            {nodes.map(node => (
              <DraggableComponent key={node.type} nodeType={node.type}>
                <Tooltip title={node.description || node.type} placement="right">
                  <div
                    className="red-ui-palette-node ui-draggable ui-draggable-handle hover:shadow-md transition-shadow duration-200"
                    style={{backgroundColor: node.color, height: "28px"}}
                  >
                    <div className="red-ui-palette-label">{node.type}</div>
                    <div className="red-ui-palette-icon-container">
                      <div
                        className="red-ui-palette-icon"
                        style={{backgroundImage: `url('/asset/images/${node.icon}')`}}
                      >
                      </div>
                    </div>
                    {node.outputs > 0 &&
                      <div className="red-ui-palette-port red-ui-palette-port-output" style={{top: "9px"}}></div>
                    }
                    {node.inputs > 0 &&
                      <div className="red-ui-palette-port red-ui-palette-port-input" style={{top: "9px"}}></div>
                    }
                  </div>
                </Tooltip>
              </DraggableComponent>
            ))}
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无组件" />
        )
      };
    }).filter(Boolean);
  }, [categorizedNodes, searchText]);

  return (
    <StyledContainer className="h-full flex flex-col p-2 border-r border-gray-200 bg-gray-50">
      <div className="mb-3 space-y-2">
        <div className="flex space-x-2">
          <Button
            onClick={save}
            type="primary"
            icon={<SaveOutlined />}
            size="middle"
            className="w-full flex items-center justify-center"
          >
            <span className="ml-1 font-medium">保存流程</span>
          </Button>
          <Button
            onClick={deploy}
            type="primary"
            danger
            icon={<RocketOutlined />}
            size="middle"
            className="w-full flex items-center justify-center"
          >
            <span className="ml-1 font-medium">部署运行</span>
          </Button>
        </div>
        <Input
          placeholder="搜索组件..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          allowClear
        />
        <SearchBox />
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ maxHeight: "calc(100vh - 180px)" }}>
        {filteredNodes.length > 0 ? (
          <Collapse
            activeKey={activeKeys}
            onChange={setActiveKeys}
            ghost
            className="palette-collapse"
            items={collapseItems}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Empty description="未找到匹配组件" />
          </div>
        )}
      </div>
    </StyledContainer>
  );
}

export default Palette;

function SearchBox() {
  const {message} = App.useApp();
  const [searchText, setSearchText] = useState("");
  const scrollToNodeByText = useEditorStore.use.scrollToNodeByText();
  
  const handleSearch = () => {
    const found = scrollToNodeByText(searchText);
    if (!found) {
      message.info("未找到匹配的节点");
    }
  };
  
  return (
    <div>
      <Input 
        value={searchText} 
        onChange={e => setSearchText(e.target.value)} 
        placeholder="搜索节点..."
        onPressEnter={handleSearch}
        prefix={<SearchOutlined />}
      />
    </div>
  );
}